import type { FC } from 'react';
import { Skeleton, SkeletonGroup } from './components/Skeleton';
import { NAV_PLACEHOLDER_LABELS } from './dashboardSections';

/**
 * Full-page placeholder in the shape of the app shell: header bar, page
 * title, three cards. Shown while the session resolves or a route chunk
 * loads, so navigation never drops to a bare dark screen.
 *
 * The header follows AppShell's breakpoints exactly (review 2026-10-18,
 * DASH-003): below lg only the brand and the account button; from lg the
 * divider, one placeholder per nav link sized by its own label (drawn from a
 * data attribute, so no text enters the DOM), the region chip and the plan
 * pill. Nothing appears that then vanishes, and nothing shifts on arrival.
 */
export const CeDashboardPageSkeleton: FC = () => (
  <SkeletonGroup
    label="Loading…"
    testId="page-skeleton"
    className="min-h-screen min-w-[300px] bg-[#1a1a1a] text-[#f5f5f5]"
  >
    <div className="fixed left-0 right-0 top-0 z-50 h-16 border-b border-white/10 bg-[#1a1a1a]">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-5">
          <Skeleton className="h-7 w-36" />
          <span aria-hidden="true" className="hidden h-5 w-px bg-white/10 lg:block" />
          <div className="flex items-center gap-1 text-sm">
            {NAV_PLACEHOLDER_LABELS.map((label) => (
              <div
                key={label}
                aria-hidden="true"
                data-testid="skeleton-nav"
                data-label={label}
                className="hidden px-2.5 py-1.5 lg:block"
              >
                <span
                  className="block animate-pulse rounded-md bg-white/10 text-transparent before:content-[attr(data-label)]"
                  data-label={label}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Region chip, then the plan pill: the real header's footprint,
              shown from lg like AppShell's. */}
          <Skeleton className="hidden h-[30px] w-11 lg:block" />
          <Skeleton className="hidden h-[30px] w-40 lg:block" />
          <Skeleton className="h-8 w-8 rounded-full" />
        </div>
      </div>
    </div>
    <div className="mx-auto max-w-7xl px-5 pb-12 pt-[104px] sm:px-6 lg:px-8 lg:pt-28">
      <div className="space-y-6">
        <div className="mb-8">
          <Skeleton className="mb-3 h-9 w-72" />
          <Skeleton className="h-4 w-96 max-w-full" />
        </div>
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-white/5 p-6">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="mt-3 h-6 w-64 max-w-full" />
            <Skeleton className="mt-5 h-12" />
          </div>
        ))}
      </div>
    </div>
  </SkeletonGroup>
);
